import * as React from 'react';
import { useHumanInTheLoop } from '@copilotkit/react-core';
import { Button, Card, Group, Stack, Text, Title } from '@mantine/core';

import Pipeline, { PipelineDefinition } from 'types/pipeline';

import { useUpdatePipeline } from 'pipelines/hooks/useUpdatePipeline';

type UpdatePipelineArgs = {
  id: string;
  definition: string;
  changesSummary: string;
};

type UpdatePipelineResult = {
  success?: boolean;
  cancelled?: boolean;
  error?: string;
};

type UpdatePipelineToolRenderProps = {
  status: string;
  args: UpdatePipelineArgs;
  respond?: (payload: UpdatePipelineResult & { pipelineId?: string }) => Promise<void>;
  result?: UpdatePipelineResult;
  updatePipeline: (payload: {
    id: string;
    definition: PipelineDefinition;
  }) => Promise<Pipeline>;
};

function UpdatePipelineToolRender({
  status,
  args,
  respond,
  result,
  updatePipeline,
}: UpdatePipelineToolRenderProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  if (status === 'complete') {
    const isSuccess = result?.success === true;
    const wasCancelled = result?.cancelled === true;

    if (wasCancelled) {
      return (
        <Card withBorder p="md" radius="md">
          <Text c="dimmed" fw={500}>
            Pipeline update cancelled
          </Text>
        </Card>
      );
    }

    if (!isSuccess) {
      return (
        <div className="w-full overflow-hidden rounded-lg bg-[#1e2738]">
          <div
            className="flex cursor-pointer items-center p-3"
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className="text-sm font-medium text-red-400">
              Pipeline update failed
            </span>
            <div className="ml-auto">
              <div className="h-2 w-2 rounded-full bg-red-400" />
            </div>
          </div>
          {isOpen && (
            <div className="px-4 pb-4 font-mono text-xs text-gray-300">
              <div className="mb-2 text-gray-400">Error:</div>
              <pre className="max-h-[200px] overflow-auto whitespace-pre-wrap">
                {result?.error || 'Unknown error'}
              </pre>
            </div>
          )}
        </div>
      );
    }

    return (
      <Card withBorder p="md" radius="md">
        <Text c="green" fw={500}>
          Pipeline updated successfully
        </Text>
      </Card>
    );
  }

  const handleConfirm = async () => {
    if (!respond) return;
    try {
      const parsedData = JSON.parse(args.definition as string);
      // The definition parameter contains the full pipeline object,
      // but useUpdatePipeline expects just the definition array
      const definitionArray = (parsedData.definition ?? parsedData) as PipelineDefinition;
      const pipeline = await updatePipeline({
        id: args.id as string,
        definition: definitionArray,
      });
      window.dispatchEvent(
        new CustomEvent('pipeline:updated', { detail: { id: args.id } })
      );
      await respond({ success: true, pipelineId: pipeline.id });
    } catch (error) {
      await respond({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  const handleReject = async () => {
    if (!respond) return;
    await respond({ success: false, cancelled: true });
  };

  return (
    <Card withBorder p="md" radius="md">
      <Stack gap="sm">
        <Title order={5}>Confirm Pipeline Update</Title>
        <Text size="sm" c="dimmed">
          The following changes will be made:
        </Text>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {args.changesSummary}
        </Text>
        {respond && (
          <Group gap="sm" mt="sm">
            <Button size="xs" onClick={handleConfirm}>
              Apply Changes
            </Button>
            <Button size="xs" variant="outline" onClick={handleReject}>
              Cancel
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

export function useUpdatePipelineTool() {
  const { mutateAsync: updatePipeline } = useUpdatePipeline();

  useHumanInTheLoop(
    {
      name: 'updatePipeline',
      description:
        'Update an existing pipeline with the specified definition. ALWAYS use it after successfully modifying a pipeline definition with the modify_pipeline_definition tool. This will prompt the user to confirm the changes before applying them.',
      parameters: [
        {
          name: 'id',
          type: 'string',
          description: 'ID of the pipeline to update',
          required: true,
        },
        {
          name: 'definition',
          type: 'string',
          description: 'JSON-encoded pipeline definition',
          required: true,
        },
        {
          name: 'changesSummary',
          type: 'string',
          description:
            'A human-readable summary of the changes being made to the pipeline',
          required: true,
        },
      ],
      render: ({ status, args, respond, result }) => (
        <UpdatePipelineToolRender
          status={status}
          args={args as UpdatePipelineArgs}
          respond={respond as UpdatePipelineToolRenderProps['respond']}
          result={result as UpdatePipelineResult}
          updatePipeline={updatePipeline}
        />
      ),
    },
    [updatePipeline]
  );
}
